import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import type { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api/utils'
import { useCallback } from 'react'

type CreditNoteLineItemRateDisplay =
  Dashboardv99990101CommonModels.CreditNoteLineItems.CreditNoteLineItemRateDisplay

type AggregatedGroup = {
  title: string
  /**
   * Each entry may represent a single invoice line item or an aggregate
   * of multiple invoice line items (rolled up when they share the same
   * tax rate). When it's a 1:1 mapping, `id` is the source invoice line
   * item ID. When multiple line items were rolled up, `id` is undefined.
   */
  lineItems: {
    id?: string
    /**
     * Source invoice line item ids for a multi-line roll-up, forwarded onto
     * the credit note so the backend can inherit the source lines' accounting
     * codes. Undefined for 1:1 lines (which use `id`) and manual lines.
     */
    invoiceLineItemIds?: string[]
    /** Source invoice line item's product, forwarded onto the credit note. */
    productId?: string
    taxRate: string
    groupId: string
    grossTotal: string
    quantity: string
    title: string
    rate: string
    servicePeriod?: {
      startDate: string
      endDate: string
    }
    /**
     * Optional, for manually entered line items. Defaults to ABSOLUTE when
     * omitted (the rolled-up invoice line item case). Other manual-only fields
     * (description, revenueRecognitionMethod) are forwarded via the spread
     * below rather than typed here, since the invoice and credit-note revenue
     * recognition method enums differ.
     */
    rateDisplay?: CreditNoteLineItemRateDisplay
  }[]
}

export const useCreateAggregatedLineItemGroups = () => {
  const { mutateAsync: postLineItemGroup } = useMutation(
    dashboardv99990101Client.postCreditNoteLineItemGroup
  )
  const { mutateAsync: postLineItem } = useMutation(
    dashboardv99990101Client.postCreditNoteLineItem
  )

  return useCallback(
    async (creditNoteId: string, aggregatedGroups: AggregatedGroup[]) => {
      await Promise.all(
        aggregatedGroups.map(async group => {
          const { lineItems, ...groupData } = group
          const res = await postLineItemGroup({
            creditNoteId,
            body: {
              ...groupData
            }
          })

          if (!res) {
            console.error('Failed to create line item group')
            return
          }

          const { id: groupId } = res

          await Promise.all(
            lineItems.map(lineItem => {
              const {
                id,
                servicePeriod,
                productId,
                invoiceLineItemIds,
                ...rest
              } = lineItem
              return postLineItem({
                creditNoteId,
                body: {
                  ...rest,
                  creditNoteId,
                  rateDisplay: lineItem.rateDisplay ?? 'ABSOLUTE',
                  externalIds: [],
                  groupId,
                  lineItemId: id,
                  invoiceLineItemIds,
                  productId,
                  servicePeriod: servicePeriod
                    ? {
                        start: servicePeriod.startDate,
                        endInclusive: servicePeriod.endDate
                      }
                    : undefined
                }
              })
            })
          )
        })
      )
    },
    [postLineItemGroup, postLineItem]
  )
}
